<template>
    <div class="tile-content tile-content-secondary">
        <r-input
            label="Title"
            :value="action.title"
            @input="updateTitle"
            placeholder="Name used in reports"/>
        <div class="divider divider-tile-content"></div>
        <split-test
            :class="{'stack-m': !requiresTabs}"
            :versions="action.emails"
            :is-form-updating="isFormUpdating"
            :delete-allowed="globalDeleteAllowed"
            :validate="$v.action.emails"
            @add-version="addEmailVersion"
            @pick-version="pickEmailVersion"
            @delete-version="deleteEmailVersion"
            @update-weight="updateEmailWeight"
        />
        <div class="stack-l">
            <div class="divider divider-tile-content"></div>
        </div>
        <v-error-messages
            :server-error="serverError"
            title="Email Errors"
            class="stack-l">
            <template v-if="validationResults.length">
                <ul class="list" :class="{'stack-m': index !== validationResults.length - 1}" v-for="(email, index) in validationResults">
                    <li>Version {{email.index + 1}}</li>
                    <li class="list-item" v-for="error in email.result">{{error.name}}</li>
                </ul>
            </template>
        </v-error-messages>
        <!-- two ore more emails -->
        <template v-if="requiresTabs">
            <r-tabs :divided="true">
                <r-tab v-for="(email, tabIndex) in action.emails" :key="tabIndex" :name="'Version ' + (tabIndex + 1)">
                    <email-editor
                        :system-event-type="systemEventType"
                        :email="email"
                        :index="tabIndex"
                        :validation-iteratee="$v.action.emails.$each.$iter"
                        :validation-bus="validationBus"
                        @input="updateEmail"/>
                </r-tab>
            </r-tabs>
        </template>
        <!-- only one email -->
        <div v-else>
            <email-editor
                :system-event-type="systemEventType"
                :email="firstEmail"
                :index="0"
                :validation-iteratee="$v.action.emails.$each.$iter"
                :validation-bus="validationBus"
                @input="updateEmail"/>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import {vCheckbox, vErrorMessages} from '@/components/form';
    import {EmailModel} from '@/common/models/rules/actions/send-email-action-model';
    import {required, between} from 'vuelidate/lib/validators';
    import placeholderEmail from '@/components/vuelidate/validators/placeholder-email';
    import ValidationAggregator from '@/common/validation/validation-aggregator';
    import getCredentialDelegate from '@/modules/rules-engine/actions/send-email/components/credentials/get-credential-delegate';
    import _ from '@/common/helpers';
    import BaseAction from '../base-action';
    import SplitTest from './components/split-test';
    import EmailEditor from './components/email-editor';

    const minOneWeightedEmail = function minOneWeightedEmail() {
        if (this.$v.action.$anyDirty) {
            return this.action.emails
                .some(e => (isNaN(e.weight) ? 0 : Number(e.weight)) > 0);
        }
        return true;
    };

    /**
     * Custom validator to check that there is at least HTML or text
     * content for the email.
     * @param {string} html
     * @param {string} text
     * @return {boolean}
     */
    const content = function content({html, text}) {
        if (this.$v.action.$anyDirty) {
            return !_.isBlank(html) || !_.isBlank(text);
        }
        return true;
    };

    export default {
        name: 'send-email',
        extends: BaseAction,
        components: {
            vCheckbox,
            vErrorMessages,
            SplitTest,
            EmailEditor,
        },
        computed: {
            requiresTabs() {
                return this.action.emails.length > 1;
            },
            globalDeleteAllowed() {
                return this.newActionPredicate(this.action);
            },
            firstEmail() {
                return this.action.emails[0];
            },
            hasCombinedValidationResults() {
                return this.validationResults.length > 0;
            },
        },
        data() {
            return {
                emailIndex: 0,
                settingsAggregator: new ValidationAggregator(),
                serverError: null,
                validationBus: new Vue(),
                validationResults: [],
            };
        },
        validations: {
            action: {
                emails: {
                    minOneWeightedEmail,
                    $each: {
                        content,
                        from: {required, placeholderEmail},
                        to: {
                            required,
                            $each: {
                                placeholderEmail,
                            },
                        },
                        cc: {
                            $each: {
                                placeholderEmail,
                            },
                        },
                        bcc: {
                            $each: {
                                placeholderEmail,
                            },
                        },
                        subject: {required},
                        weight: {
                            between: between(0, 100),
                            minOneWeightedEmail,
                        },
                    },
                },
            },
        },
        methods: {
            dispatchUpdate(data) {
                this.$emit('input', data);
            },
            updateTitle(title) {
                this.dispatchUpdate(this.action.cloneSelf({title}));
            },
            smartClone() {
                return this.action.cloneSelf();
            },
            addEmailVersion() {
                const copy = this.smartClone();
                copy.emails.push(new EmailModel());
                copy.recalculateWeights();
                this.dispatchUpdate(copy);
            },
            spliceEmails(index, injection) {
                const copy = this.smartClone();
                if (injection) {
                    copy.emails.splice(index, 1, injection);
                } else {
                    copy.emails.splice(index, 1);
                }
                copy.recalculateWeights();
                this.dispatchUpdate(copy);
            },
            deleteEmailVersion(index) {
                this.spliceEmails(index);
            },
            pickEmailVersion(winnerIndex) {
                const copy = this.smartClone();
                copy.emails = copy.emails.splice(winnerIndex, 1);
                this.dispatchUpdate(copy);
            },
            updateEmailWeight({index, weight}) {
                const copy = this.smartClone();
                copy.emails[index].weight = weight;
                this.dispatchUpdate(copy);
            },
            getValidationAggregate() {
                return {
                    name: 'send-email',
                    beforeValidation: () => this.$v.$touch(),
                    rules: [
                        {
                            name: `Please verify that all required email fields are provided.`,
                            delegate: () => {
                                this.resetValidationResults();
                                this.validationBus.$emit('validate');
                                // check that there are no email errors other
                                // then the aggregated ones
                                return this.hasCombinedValidationResults
                                    || this.$v.action.emails.$error;
                            },
                        },
                        {
                            name: `At least one email version in the split test must have a weight greater than zero.`,
                            delegate: () => !this.$v.action.emails.minOneWeightedEmail,
                        },
                    ],
                };
            },
            updateEmail({index, emailVersion}) {
                this.spliceEmails(index, emailVersion);
            },
            async asyncSaveStep() {
                // generate credentials for emails without hashes
                const credentialChain = this.action.emails
                    .filter(email => email.$credentials !== null)
                    .map((email) => {
                        const data = email.$credentials.getPayload();
                        return (async () => {
                            const {fields} = await this.$api(
                                this.getCredentialDelegate(data, email.$credentials),
                            );
                            // update email hash with new credential value
                            email.credentialHash = fields.hash;
                        })();
                    });
                await Promise.all(credentialChain);
            },
            getCredentialDelegate(...args) {
                return getCredentialDelegate(...args);
            },
            resetValidationResults() {
                this.validationResults = [];
            },
        },
        mounted() {
            this.validationBus.$on('touch', () => this.$v.$touch());
            this.validationBus.$on('reset', () => this.resetValidationResults());
            this.validationBus.$on('result', data => data.result && this.validationResults.push(data));
        },
    };
</script>
